import {LocationQuery} from 'vue-router';

/**
 * Filter 필드 타입 정의
 */
export type FilterValue = string | number | boolean | null | undefined;

export interface FilterFieldConfig<T = FilterValue> {
    /** 기본값 */
    defaultValue: T;
    /** query string → state 변환 함수 */
    parse?: (value: string | null) => T;
    /** state → query string 변환 함수 */
    serialize?: (value: T) => string | null;
    /** 유효성 검사 함수 (false 반환 시 기본값 사용) */
    validate?: (value: T) => boolean;
}

export type FilterSchema<T extends Record<string, FilterValue>> = {
    [K in keyof T]: FilterFieldConfig<T[K]>;
};

export interface FilterOptions<T extends Record<string, FilterValue>> {
    /** 필터 스키마 정의 */
    schema: FilterSchema<T>;
    /** 필터 스키마 전체 유효성 검사 */
    validate?: (value: T) => T;
}

/**
 * 기본값으로 초기화된 필터 객체 생성
 */
export const createDefaultFilter = <T extends Record<string, FilterValue>>(
    schema: FilterSchema<T>,
    validate?: (value: T) => T,
): T => {
    const defaultParams = Object.keys(schema).reduce((acc, key) => {
        acc[key as keyof T] = schema[key].defaultValue;
        return acc;
    }, {} as T);
    return validate?.(defaultParams) ?? defaultParams;
};

/**
 * URL Query를 필터 객체로 파싱
 */
export const parseQueryToFilter = <T extends Record<string, FilterValue>>(
    query: LocationQuery,
    schema: FilterSchema<T>,
    validate?: (value: T) => T,
): T => {
    const filter = createDefaultFilter(schema, validate);

    for (const key of Object.keys(schema) as (keyof T)[]) {
        const config = schema[key];
        const queryValue = query[key as string];

        if (queryValue === undefined || queryValue === null) {
            continue;
        }

        const rawValue = Array.isArray(queryValue) ? queryValue[0] : queryValue;
        const parsedValue = config.parse
            ? config.parse(rawValue)
            : (rawValue as T[keyof T]);

        if (config.validate && !config.validate(parsedValue)) {
            continue;
        }

        filter[key] = parsedValue;
    }

    return validate?.(filter) ?? filter;
};

/**
 * 필터 객체를 URL Query로 변환
 */
export const serializeFilterToQuery = <T extends Record<string, FilterValue>>(
    filter: T,
    schema: FilterSchema<T>,
): Record<string, string> => {
    const query: Record<string, string> = {};

    for (const key of Object.keys(schema) as (keyof T)[]) {
        const config = schema[key];
        const value = filter[key];

        const serialized = config.serialize
            ? config.serialize(value)
            : value?.toString() ?? null;

        if (serialized !== null && serialized !== '') {
            query[key as string] = serialized;
        }
    }

    return query;
};
